import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workbookService } from '../services/workbookService';
import { decryptPDF } from '../core/workbook/pdfDecryptor';
import { initSecurityLayer } from '../core/workbook/security';
import { saveAnnotationsLocal, loadAnnotationsLocal } from '../core/workbook/annotationDB';
import { loadPdfFromMemory } from '../core/workbook/pdfiumEngine';
import SecurePDFCanvas from '../components/Workbook/SecurePDFCanvas';
import AnnotationLayer from '../components/Workbook/AnnotationLayer';
import FloatingToolbar from '../components/Workbook/FloatingToolbar';
import './workbook.css';
import DownloadProgressModal from '../components/Workbook/DownloadProgressModal';

const UPLOAD_BASE = process.env.REACT_APP_UPLOAD_URL || '/upload';
const VIEW_MODE_KEY = 'workbook-view-mode';

// ============================================================
// Hook: عرض دقیق viewport دستگاه
// ------------------------------------------------------------
// از document.documentElement.clientWidth استفاده می‌کنیم که
// عرض محتوایی قابل‌استفاده (بدون اسکرول‌بار عمودی) را می‌دهد.
// این مقدار = عرض دقیق دستگاه کاربر.
// ============================================================
const useViewportWidth = () => {
  const [width, setWidth] = useState(() => {
    if (typeof document === 'undefined') return 0;
    return document.documentElement.clientWidth || window.innerWidth || 0;
  });

  useLayoutEffect(() => {
    let raf = null;
    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = document.documentElement.clientWidth || window.innerWidth || 0;
        if (w > 0) setWidth((prev) => (prev !== w ? w : prev));
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    let ro = null;
    try {
      ro = new ResizeObserver(update);
      ro.observe(document.documentElement);
    } catch {}
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      if (ro) ro.disconnect();
    };
  }, []);

  return width;
};

// ============================================================
// PdfPage — رندر یک صفحه PDF با PDFium
// ------------------------------------------------------------
// • عرض دقیقاً = عرض viewport دستگاه
// • ارتفاع از aspect-ratio اصلی صفحه
// • در حالت دست: pointer-events: none → native scroll
// • در حالت رسم: pointer-events: auto → تعامل کامل
// • onPointerDownCapture: هر لمس، صفحه را فعال می‌کند
// • IntersectionObserver: فقط برای lazy render
// ============================================================
function PdfPage({
  pageNumber,
  pdfDocument,
  availableWidth,
  naturalSize,
  isActive,
  initialStrokes,
  user,
  annotationsTick,
  activeTool,
  toolSettings,
  eraserMode,
  onRegisterAPI,
  onStateChange,
  onActivate,
}) {
  const wrapperRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);
  // 🆕 اندازه‌گیری عرض واقعی برای جلوگیری از باگ تغییر سایز
  const [actualWidth, setActualWidth] = useState(availableWidth > 0 ? availableWidth : 320);

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setActualWidth(w);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ===== حفاظت در برابر مقادیر نامعتبر =====
  const naturalW = naturalSize?.width > 0 ? naturalSize.width : 595;
  const naturalH = naturalSize?.height > 0 ? naturalSize.height : 842;

  // ===== عرض دقیق = عرض واقعی اندازه‌گیری شده =====
  const displayWidth = actualWidth;

  // ===== scale = نسبت عرض نمایشی به عرض طبیعی =====
  const scale = displayWidth / naturalW;

  // ===== ارتفاع متناسب با نسبت تصویر =====
  const displayHeight = displayWidth * (naturalH / naturalW);

  // ===== نسبت تصویر برای CSS =====
  const aspectRatio = `${naturalW} / ${naturalH}`;

  // ===== ابزار دست؟ =====
  const isHand = activeTool === 'hand';

  // ===== Lazy render با IntersectionObserver =====
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setShouldRender(true);
        });
      },
      { rootMargin: '700px 0px', threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ===== فعال‌سازی صفحه با هر لمس (Capture Phase) =====
  const handleActivate = useCallback(() => {
    if (onActivate && !isActive) {
      onActivate(pageNumber);
    }
  }, [onActivate, pageNumber, isActive]);

  return (
    <div
      ref={wrapperRef}
      className={`pdf-page-wrapper ${isActive ? 'is-active' : 'is-inactive'}`}
      style={{
        width: '100%', // 🆕 عرض را به CSS می‌سپاریم تا با اسکرول‌بار هماهنگ شود
        maxWidth: `${availableWidth}px`, // 🆕 حداکثر عرض مجاز
        aspectRatio, // 🆕 ارتفاع خودکار بر اساس نسبت تصویر
      }}
      onPointerDownCapture={handleActivate}
      data-page={pageNumber}
    >
      {shouldRender && pdfDocument ? (
        <>
          <SecurePDFCanvas
            pdfDocument={pdfDocument}
            pageNumber={pageNumber}
            scale={scale}
            userId={user.id}
            userName={user.fullName || user.name}
          />
          {/* ===== لایه یادداشت =====
              • در حالت دست: pointer-events: none → native scroll
              • در حالت رسم: pointer-events: auto → تعامل کامل
          */}
          <div
            className="annotation-layer-wrap"
            style={{ pointerEvents: isHand ? 'none' : 'auto' }}
          >
            <AnnotationLayer
              key={`ann-${pageNumber}-${annotationsTick}`}
              pageNumber={pageNumber}
              width={Math.round(displayWidth)}
              height={Math.round(displayHeight)}
              initialStrokes={initialStrokes}
              onRegisterAPI={(api) => onRegisterAPI(pageNumber, api)}
              onStateChange={isActive ? onStateChange : undefined}
              activeTool={activeTool}
              toolSettings={toolSettings}
              eraserMode={eraserMode}
            />
          </div>
        </>
      ) : (
        <div className="page-placeholder">
          <div className="page-placeholder-spinner" />
          <span>صفحه {pageNumber}</span>
        </div>
      )}
    </div>
  );
}









// ============================================================
// fetchWithProgress
// ------------------------------------------------------------
// استریم دانلود با گزارش پیشرفت لحظه‌ای
// ============================================================
async function fetchWithProgress(url, onProgress) {
  const response = await fetch(url);
  if (!response.ok) throw new Error('خطا در دریافت فایل');

  const contentLength = parseInt(
    response.headers.get('Content-Length') || '0',
    10
  );

  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;
  const startTime = Date.now();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    received += value.length;

    const elapsed = (Date.now() - startTime) / 1000;
    const speed = elapsed > 0 ? received / elapsed : 0;

    if (onProgress) {
      onProgress({
        received,
        total: contentLength,
        speed,
        percent:
          contentLength > 0
            ? Math.min(100, (received / contentLength) * 100)
            : 0,
      });
    }
  }

  // ادغام همه chunk‌ها در یک Uint8Array
  const result = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  return result.buffer;
}











// ============================================================
// کامپوننت اصلی
// ============================================================
const WorkbookViewer = () => {
  const { bookId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  // ===== Refs =====
  const securityRef = useRef(null);
  const scrollRef = useRef(null);
  const apiRef = useRef(null);
  const pageApisRef = useRef(new Map());
  const prevActivePageRef = useRef(1);
  const lastSavedRef = useRef(null);
  const activePageRef = useRef(1);
  const pdfDocumentRef = useRef(null);

  // ===== State: هسته =====
  const [pdfDocument, setPdfDocument] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSizes, setPageSizes] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadPercent, setLoadPercent] = useState(0); // 🆕 استیت درصد پیشرفت
  const [loadPhase, setLoadPhase] = useState('در حال آماده‌سازی...'); // 🆕 استیت متن مرحله
  const [error, setError] = useState(null);
  const [annotations, setAnnotations] = useState({});
  const [annotationsTick, setAnnotationsTick] = useState(0);

  // ===== State: حالت نمایش (ذخیره‌شده در localStorage) =====
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem(VIEW_MODE_KEY);
      return saved === 'continuous' ? 'continuous' : 'single';
    } catch {
      return 'single';
    }
  });

  // ===== State: ابزارها =====
  const [activeTool, setActiveTool] = useState('hand');
  const [toolSettings, setToolSettings] = useState({
    color: '#000000',
    size: 3,
    opacity: 0.35,
  });
  const [eraserMode, setEraserMode] = useState('area');
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [apiState, setApiState] = useState({ canUndo: false, canRedo: false });




  // ===== State: تم تیره/روشن =====
const [isDark, setIsDark] = useState(() => {
  try {
    return localStorage.getItem('workbook-theme-dark') === '1';
  } catch {
    return false;
  }
});

// ===== State: مودال پیشرفت دانلود =====
const [downloadProgress, setDownloadProgress] = useState({
  open: false,
  received: 0,
  total: 0,
  speed: 0,
  percent: 0,
});

useEffect(() => {
  try {
    localStorage.setItem('workbook-theme-dark', isDark ? '1' : '0');
  } catch {}
}, [isDark]);


  // ============================================================
  // عرض دقیق دستگاه (viewport)
  // ============================================================
  const viewportWidth = useViewportWidth();
  const availableWidth = viewportWidth > 0 ? viewportWidth : 320;

  // ===== نگه‌داشتن activePage در ref =====
  useEffect(() => {
    activePageRef.current = activePage;
  }, [activePage]);

  // ===== نگه‌داشتن pdfDocument در ref (برای cleanup) =====
  useEffect(() => {
    pdfDocumentRef.current = pdfDocument;
  }, [pdfDocument]);

  // ===== ذخیره viewMode در localStorage =====
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch {}
  }, [viewMode]);

  // ===== در single mode، activePage = pageNumber =====
  useEffect(() => {
    if (viewMode === 'single') setActivePage(pageNumber);
  }, [viewMode, pageNumber]);

  // ============================================================
  // بارگذاری امن کتاب کار (رمزگشایی + PDFium)
  // ============================================================
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setLoadPhase('بررسی لایسنس...');
        setLoadPercent(5);

        // ۱. بررسی لایسنس
        const license = await workbookService.checkLicense(user.id, bookId);
        if (!license) { navigate('/books'); return; }
        if (license.expired) {
          setError('لایسنس شما منقضی شده است.');
          setLoading(false);
          return;
        }

        // ۲. session امن
        setLoadPhase('ایجاد اتصال امن...');
        setLoadPercent(10);
        const session = await workbookService.getSecureSession(user.id, bookId);

        // ۳. کلید رمزگشایی
        const keyRes = await fetch(
          `${UPLOAD_BASE}/api/secure-pdf/key/${session.sessionToken}`,
          { method: 'POST' }
        );
        const { key, iv } = await keyRes.json();

        // ۴. استریم فایل رمزنگاری‌شده
setDownloadProgress({
  open: true,
  received: 0,
  total: 0,
  speed: 0,
  percent: 0,
});

let encrypted;
try {
  setLoadPhase('دانلود فایل کتاب...');
  encrypted = await fetchWithProgress(
    `${UPLOAD_BASE}/api/secure-pdf/stream/${session.sessionToken}`,
    (p) => {
      setDownloadProgress((prev) => ({ ...prev, ...p }));
      // 🆕 مپ کردن درصد دانلود به ۱۵٪ تا ۷۰٪ از کل لودینگ
      setLoadPercent(15 + Math.round(p.percent * 0.55));
    }
  );
} finally {
  setDownloadProgress((prev) => ({ ...prev, open: false }));
}

        if (cancelled) return;

        // ۵. رمزگشایی → PDF اصلی
        setLoadPhase('رمزگشایی امن فایل...');
        setLoadPercent(75);
        const decrypted = await decryptPDF(encrypted, key, iv);
        if (cancelled) return;

        // ۶. بارگذاری PDF در PDFium (مستقیم، بدون هیچ تبدیل)
        setLoadPhase('آماده‌سازی صفحات...');
        setLoadPercent(90);
        const pdfData = new Uint8Array(decrypted);
        const doc = await loadPdfFromMemory(pdfData);

        if (cancelled) {
          try { doc.close(); } catch {}
          return;
        }

        if (doc.hasPassword) {
          throw new Error('فایل PDF رمزدار است');
        }

        // ۷. ذخیره همه اطلاعات
        setPdfDocument(doc);
        setTotalPages(doc.pageCount);
        setPageSizes(doc.pageSizes);
        setLoading(false);
      } catch (err) {
        console.error('❌', err);
        if (!cancelled) {
          setError(err.message || 'خطا در بارگذاری');
          setLoading(false);
        }
      }
    };

    if (user?.id) load();
    return () => { cancelled = true; };
  }, [user?.id, bookId, navigate]);

  // ============================================================
  // آزادسازی منابع PDFium هنگام unmount
  // ============================================================
  useEffect(() => {
    return () => {
      if (pdfDocumentRef.current) {
        try { pdfDocumentRef.current.close(); } catch {}
      }
    };
  }, []);

  // ============================================================
  // لایه امنیتی (blur, disable right-click, etc.)
  // ============================================================
  useEffect(() => {
    if (!securityRef.current) return;
    return initSecurityLayer(securityRef.current);
  }, []);

  // ============================================================
  // بارگذاری یادداشت‌ها (localStorage + سرور با اولویت آخرین زمان)
  // ============================================================
useEffect(() => {
  if (!user?.id || !bookId) return;
  (async () => {
    const map = {};

    // ۱. اول سرور (منبع حقیقت)
    try {
      const server = await workbookService.loadAnnotations(user.id, bookId);
      server.forEach((a) => { map[a.pageNumber] = a.strokes; });
    } catch {}

    // ۲. IndexedDB فقط برای پر کردن جای خالی (کش)
    try {
      const local = await loadAnnotationsLocal(user.id, bookId);
      local.forEach((a) => {
        if (!map[a.pageNumber]) map[a.pageNumber] = a.strokes;
      });
    } catch {}

    setAnnotations(map);
  })();
}, [user?.id, bookId]);

  // ============================================================
  // ثبت API هر صفحه
  // ============================================================
  const handleRegisterAPI = useCallback((pageNum, api) => {
    pageApisRef.current.set(pageNum, api);
    if (pageNum === activePageRef.current) {
      apiRef.current = api;
    }
  }, []);

  const handlePageStateChange = useCallback((pageNum, state) => {
    if (pageNum === activePageRef.current) {
      setApiState(state);
    }
  }, []);

  // به‌روزرسانی apiRef و reset state با تغییر activePage
  useEffect(() => {
    apiRef.current = pageApisRef.current.get(activePage) || null;
    setApiState({ canUndo: false, canRedo: false });
  }, [activePage]);

  // ============================================================
  // ذخیره صفحه‌ای مشخص
  // ============================================================
const savePage = useCallback(async (pageNum, strokes) => {
  if (!user?.id || !bookId || !pageNum) return;
  try {
    // ۱. اول state (خوش‌بینانه)
    setAnnotations((prev) => ({ ...prev, [pageNum]: strokes }));

    // ۲. سرور (منبع حقیقت)
    await workbookService.saveAnnotations(user.id, bookId, pageNum, strokes);

    // ۳. IndexedDB فقط به‌عنوان کش (اختیاری)
    saveAnnotationsLocal(user.id, bookId, pageNum, strokes).catch(() => {});
  } catch (e) {
    console.warn('خطا در ذخیره:', e);
    throw e;
  }
}, [user?.id, bookId]);

  // ============================================================
  // ذخیره دستی (دکمه 💾)
  // ============================================================
  const handleSave = useCallback(async (pageNum, strokes) => {
    setSaving(true);
    setSyncStatus('saving');
    try {
      await savePage(pageNum, strokes);
      setSyncStatus('saved');
      setTimeout(() => setSyncStatus('idle'), 2000);
    } catch {
      setSyncStatus('error');
    } finally {
      setSaving(false);
    }
  }, [savePage]);

  // ============================================================
  // ذخیره خودکار قبل از تغییر صفحه فعال
  // ============================================================
useEffect(() => {
  const prevPage = prevActivePageRef.current;
  if (prevPage === activePage) return;

  const prevApi = pageApisRef.current.get(prevPage);
  const prevStrokes = prevApi?.getStrokes() || [];
  if (prevStrokes.length > 0) {
    setAnnotations((prev) => ({ ...prev, [prevPage]: prevStrokes }));
    // سرور اول
    workbookService.saveAnnotations(user.id, bookId, prevPage, prevStrokes).catch(() => {});
    // IndexedDB کش
    saveAnnotationsLocal(user.id, bookId, prevPage, prevStrokes).catch(() => {});
  }
  prevActivePageRef.current = activePage;
}, [activePage, user?.id, bookId]);

  // ============================================================
  // ناوبری در single mode
  // ============================================================
const goToPage = useCallback((n) => {
  if (n < 1 || n > totalPages || n === pageNumber) return;
  const currentStrokes = apiRef.current?.getStrokes() || [];
  if (currentStrokes.length > 0) {
    setAnnotations((prev) => ({ ...prev, [pageNumber]: currentStrokes }));
    workbookService.saveAnnotations(user.id, bookId, pageNumber, currentStrokes).catch(() => {});
    saveAnnotationsLocal(user.id, bookId, pageNumber, currentStrokes).catch(() => {});
  }
  setPageNumber(n);
}, [pageNumber, totalPages, user?.id, bookId]);

  // ============================================================
  // تغییر حالت نمایش
  // ============================================================
  const switchViewMode = useCallback((mode) => {
    if (mode === viewMode) return;

    // ذخیره صفحه فعلی قبل از تغییر
const currentStrokes = apiRef.current?.getStrokes() || [];
if (currentStrokes.length > 0) {
  const currentPage = viewMode === 'single' ? pageNumber : activePageRef.current;
  setAnnotations((prev) => ({ ...prev, [currentPage]: currentStrokes }));
  workbookService.saveAnnotations(user.id, bookId, currentPage, currentStrokes).catch(() => {});
  saveAnnotationsLocal(user.id, bookId, currentPage, currentStrokes).catch(() => {});
}

    // 🆕 continuous → single: صفحه‌ی فعلی رو در pageNumber sync کن
    if (viewMode === 'continuous' && mode === 'single') {
      setPageNumber(activePageRef.current);
    }

    setViewMode(mode);
    setAnnotationsTick((t) => t + 1);

    // اسکرول به صفحه فعلی
    setTimeout(() => {
      const targetPage = viewMode === 'single' ? pageNumber : activePageRef.current;
      if (mode === 'continuous' && scrollRef.current) {
        const pageEl = scrollRef.current.querySelector(`[data-page="${targetPage}"]`);
        if (pageEl) pageEl.scrollIntoView({ behavior: 'instant', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    }, 50);
  }, [viewMode, pageNumber, user?.id, bookId]);

  // ============================================================
  // ذخیره خودکار دوره‌ای (هر ۳۰ ثانیه)
  // ============================================================
  useEffect(() => {
    if (!user?.id || !bookId) return;

    const interval = setInterval(async () => {
      const currentStrokes = apiRef.current?.getStrokes() || [];
      const json = JSON.stringify(currentStrokes);
      if (json === lastSavedRef.current || currentStrokes.length === 0) return;

      try {
        const pageToSave = viewMode === 'single' ? pageNumber : activePageRef.current;
        await saveAnnotationsLocal(user.id, bookId, pageToSave, currentStrokes);
        setAnnotations((prev) => ({ ...prev, [pageToSave]: currentStrokes }));
        lastSavedRef.current = json;
        setSyncStatus('saved');
        setTimeout(() => setSyncStatus('idle'), 2000);
      } catch {}
    }, 30000);

    return () => clearInterval(interval);
  }, [user?.id, bookId, pageNumber, viewMode]);

  // ============================================================
  // ذخیره اضطراری در beforeunload
  // ============================================================
  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentStrokes = apiRef.current?.getStrokes() || [];
      if (currentStrokes.length > 0) {
        const pageToSave = viewMode === 'single' ? pageNumber : activePageRef.current;
        try {
          localStorage.setItem(
            `workbook-emergency-${user.id}-${bookId}-${pageToSave}`,
            JSON.stringify(currentStrokes)
          );
        } catch {}
        saveAnnotationsLocal(user.id, bookId, pageToSave, currentStrokes).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user?.id, bookId, pageNumber, viewMode]);

  // ============================================================
  // بازیابی اضطراری
  // ============================================================
  useEffect(() => {
    if (!user?.id || !bookId) return;
    const pageToCheck = viewMode === 'single' ? pageNumber : activePageRef.current;
    const key = `workbook-emergency-${user.id}-${bookId}-${pageToCheck}`;
    const emergency = localStorage.getItem(key);
    if (emergency) {
      try {
        const strokes = JSON.parse(emergency);
        if (strokes.length > 0) {
          setAnnotations((prev) => ({ ...prev, [pageToCheck]: strokes }));
        }
        localStorage.removeItem(key);
      } catch {}
    }
  }, [user?.id, bookId, pageNumber, viewMode]);

  // ============================================================
  // رندر
  // ============================================================
  if (loading) {
    // 🆕 محاسبه محیط دایره برای SVG
    const radius = 54;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (loadPercent / 100) * circumference;

    return (
      <div className={`wb-loading-screen ${isDark ? 'dark-theme' : 'light-theme'}`}>
        <div className="wb-loading-card">
          <div className="wb-circle-progress">
            <svg width="140" height="140" viewBox="0 0 140 140">
              {/* حلقه پس‌زمینه */}
              <circle
                cx="70" cy="70" r={radius}
                fill="none"
                strokeWidth="8"
                className="wb-circle-bg"
              />
              {/* حلقه پیشرفت متحرک */}
              <circle
                cx="70" cy="70" r={radius}
                fill="none"
                strokeWidth="8"
                strokeLinecap="round"
                className="wb-circle-fill"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                transform="rotate(-90 70 70)"
              />
            </svg>
            <div className="wb-percent-text">
              <span className="wb-percent-num">{loadPercent}%</span>
            </div>
          </div>
          
          <h2 className="wb-loading-title">کتاب‌خانه امن</h2>
          <p className="wb-loading-phase">{loadPhase}</p>
          
          <div className="wb-security-badges">
            <span>🔒 اتصال رمزنگاری‌شده</span>
            <span>⚡️ سرعت بالا</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workbook-error">
        <span>⚠️</span>
        <p>{error}</p>
        <button onClick={() => navigate('/books')}>بازگشت</button>
      </div>
    );
  }

  return (
    <div className={`wb-root ${isDark ? 'wb-dark' : ''}`} ref={securityRef}>
      {/* ========== هدر ========== */}
      <div className="workbook-header">
        <div className="workbook-header-left">
          <button className="btn-back" onClick={() => navigate('/books')}>
            ← بازگشت
          </button>
          <div className="view-mode-toggle" role="group" aria-label="حالت نمایش">
            <button
              className={`mode-btn ${viewMode === 'single' ? 'active' : ''}`}
              onClick={() => switchViewMode('single')}
              title="تک صفحه‌ای"
              aria-label="تک صفحه‌ای"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="3" width="14" height="18" rx="2" />
                <line x1="9" y1="8" x2="15" y2="8" />
                <line x1="9" y1="12" x2="15" y2="12" />
                <line x1="9" y1="16" x2="13" y2="16" />
              </svg>
              <span>تک صفحه</span>
            </button>
            <button
              className={`mode-btn ${viewMode === 'continuous' ? 'active' : ''}`}
              onClick={() => switchViewMode('continuous')}
              title="کشویی"
              aria-label="کشویی"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="9" rx="1.5" />
                <rect x="5" y="13" width="14" height="9" rx="1.5" />
              </svg>
              <span>کشویی</span>
            </button>
          </div>
        </div>

        <span className="page-indicator">
          {viewMode === 'single'
            ? `صفحه ${pageNumber} از ${totalPages}`
            : `صفحه ${activePage} از ${totalPages}`}
        </span>

        <div className="sync-indicator">
          {syncStatus === 'saving' && '⏳ در حال ذخیره...'}
          {syncStatus === 'saved' && '✅ ذخیره شد'}
          {syncStatus === 'error' && '❌ خطا'}
        </div>
      </div>

      {/* ========== محتوای PDF ========== */}
      <div
        ref={scrollRef}
        className={`workbook-content ${viewMode === 'continuous' ? 'mode-continuous' : 'mode-single'}`}
        style={{ overflowY: 'scroll' }} // 🆕 قفل کردن اسکرول‌بار برای جلوگیری از پرش
      >
        {viewMode === 'single' ? (
          <PdfPage
            key={`single-${pageNumber}-${annotationsTick}`}
            pageNumber={pageNumber}
            pdfDocument={pdfDocument}
            availableWidth={availableWidth}
            naturalSize={pageSizes[pageNumber - 1] || { width: 595, height: 842 }}
            isActive={true}
            initialStrokes={annotations[pageNumber] || []}
            user={user}
            annotationsTick={annotationsTick}
            activeTool={activeTool}
            toolSettings={toolSettings}
            eraserMode={eraserMode}
            onRegisterAPI={handleRegisterAPI}
            onStateChange={(state) => handlePageStateChange(pageNumber, state)}
            onActivate={() => {}}
          />
        ) : (
          <div className="continuous-scroll">
            {Array.from({ length: totalPages }, (_, i) => {
              const p = i + 1;
              return (
                <PdfPage
                  key={`page-${p}-${annotationsTick}`}
                  pageNumber={p}
                  pdfDocument={pdfDocument}
                  availableWidth={availableWidth}
                  naturalSize={pageSizes[i] || { width: 595, height: 842 }}
                  isActive={activePage === p}
                  initialStrokes={annotations[p] || []}
                  user={user}
                  annotationsTick={annotationsTick}
                  activeTool={activeTool}
                  toolSettings={toolSettings}
                  eraserMode={eraserMode}
                  onRegisterAPI={handleRegisterAPI}
                  onStateChange={(state) => handlePageStateChange(p, state)}
                  onActivate={setActivePage}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ========== ناوبری صفحه ========== */}
      {viewMode === 'single' && (
        <div className="workbook-nav">
          <button
            onClick={() => goToPage(pageNumber - 1)}
            disabled={pageNumber <= 1}
          >
            ← قبلی
          </button>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={pageNumber}
            onChange={(e) => goToPage(+e.target.value)}
          />
          <button
            onClick={() => goToPage(pageNumber + 1)}
            disabled={pageNumber >= totalPages}
          >
            بعدی →
          </button>
        </div>
      )}

      {/* ========== نوار ابزار شناور ========== */}
      <FloatingToolbar
        activeTool={activeTool}
        setActiveTool={setActiveTool}
        toolSettings={toolSettings}
        setToolSettings={setToolSettings}
        onClearAll={() => {
          apiRef.current?.clearAll();
          const pageToSave = viewMode === 'single' ? pageNumber : activePageRef.current;
          handleSave(pageToSave, []);
        }}
        onSave={() => {
          const pageToSave = viewMode === 'single' ? pageNumber : activePageRef.current;
          handleSave(pageToSave, apiRef.current?.getStrokes() || []);
        }}
        saving={saving}
        eraserMode={eraserMode}
        setEraserMode={setEraserMode}
        onUndo={() => apiRef.current?.undo()}
        onRedo={() => apiRef.current?.redo()}
        canUndo={apiState.canUndo}
        canRedo={apiState.canRedo}
      />
      {/* ========== مودال پیشرفت دانلود ========== */}
<DownloadProgressModal
  open={downloadProgress.open}
  progress={downloadProgress}
/>
    </div>
  );
};

export default WorkbookViewer;